<script lang="ts" setup>
import { computed, inject, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { i18n } from '@/i18n';
import RecommendEditSupplierAnnounce from '@/recommendOrderModule/pages/RecommendEditSupplierAnnounce.vue';
import RecommendEditReceiveAnnounce from '@/recommendOrderModule/pages/RecommendEditReceiveAnnounce.vue';
import RecommendEditDefinedOrder from '@/recommendOrderModule/pages/RecommendEditDefinedOrder.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { simpleCopy } from '@/utils/deepCopy';
import { key } from './useRecommendOrderStore';
import { IRecommendOrderArrivalForRecommendEdit, RecommendEditProgress } from './interfaces/recommendOrder';
import { IAnnounce } from '@/coreDomainsModule/entities/forecast';
import { IExtraSimulate } from './interfaces/extraSimulation';
import { ForecastService } from '@/coreDomainsModule/services/forecastService';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { IYearMonth } from '@/coreDomainsModule/date';
import { emptyInventory } from '@/coreDomainsModule/entities/inventory';
import { FORECAST_LENGTH_IN_MODAL } from '@/coreDomainsModule/constants';
import { DEFAULT_DISPLAY_FORECAST_MONTH } from '../constants';
import { openConfirmModal } from '@/corePagesModule/components/modal/modalActions';
import { IArrival } from '@/coreDomainsModule/entities/arrival';
import TextIconButton from '@/corePagesModule/components/TextIconButton.vue';
import RecommendEditWrapper from '../components/RecommendEditWrapper.vue';

/**
 * uiState
 */
interface IRecommendEdit {
  recommendOrderForms: IRecommendOrderArrivalForRecommendEdit[]; // 推奨発注量
  partialDeliveryForms: IArrival[]; // 分納情報
  checkedIndex: number[];
  useCustomerAnnounce: boolean;
  selectedYearMonth?: IYearMonth;
  monthsRegisterSupplierForecast: number;
  isLastCheckedUserNameEllipsis: boolean;
}

const props = withDefaults(
  defineProps<{
    progress?: RecommendEditProgress;
    recommend?: IRecommendOrderArrivalForRecommendEdit[];
    partialDeliveryForms?: IArrival[];
    propExtraSimulate?: IExtraSimulate;
    customerAnnounce?: IAnnounce[];
    supplierAnnounce?: IAnnounce[];
    propCheckedIndex?: number[];
    definedOrderScrollPosition?: { x: number; y: number };
  }>(),
  {
    progress: 'EditReceiveAnnounce',
    recommend: undefined,
    partialDeliveryForms: undefined,
    propExtraSimulate: undefined,
    customerAnnounce: undefined,
    supplierAnnounce: undefined,
    propCheckedIndex: undefined,
    // eslint-disable-next-line jsdoc/require-jsdoc
    definedOrderScrollPosition: () => ({ x: 0, y: 0 }),
  }
);

const emits = defineEmits<{
  (e: 'updateBusiness'): void;
  (e: 'saveDefinedOrderScrollPosition', x: number, y: number);
}>();

// style variables

//変数
const t = i18n.t;

const store = inject(key);

if (!store) {
  throw new Error('store is undefined');
}

const state: IRecommendEdit = reactive<IRecommendEdit>({
  recommendOrderForms: [],
  partialDeliveryForms: [],
  checkedIndex: [],
  useCustomerAnnounce: true,
  selectedYearMonth: undefined,
  monthsRegisterSupplierForecast: DEFAULT_DISPLAY_FORECAST_MONTH - 1,
  isLastCheckedUserNameEllipsis: false,
}) as IRecommendEdit;

const definedOrder = ref<any | null>(null);

const lastCheckedAt = computed(() =>
  store.state.formSet.lastCheckedAt
    ? DateService.convertDateToYYYYMMDDHHMM(store.state.formSet.lastCheckedAt)
    : DateService.convertDateToYYYYMMDDHHMMAllHyphen()
);

// イベントフック
onMounted(() => {
  state.recommendOrderForms = simpleCopy(props.recommend ?? []);
  state.partialDeliveryForms = simpleCopy(props.partialDeliveryForms ?? []);
  state.checkedIndex = simpleCopy(props.propCheckedIndex ?? []);
});

onBeforeUnmount(() => {
  const scrollY = definedOrder.value?.tableRef.scrollTop;
  emits('saveDefinedOrderScrollPosition', 0, scrollY ?? 0);
});

// 直近26ヶ月の受領内示を生成する
const getFourMonthCustomerAnnouncesFromArrivals = computed(() => {
  const announces: IAnnounce[] = [];
  ForecastService.setAnnounceYearMonth(announces, new Date(), false, FORECAST_LENGTH_IN_MODAL);
  props.propExtraSimulate?.extraAnnounce?.forEach((it) => {
    const announce = announces.find((announce) => DateService.equalYearMonthInfo(announce, it));
    if (announce) {
      announce.amount = it.amount;
    }
  });
  return announces;
});

// 直近26ヶ月の仕入れ先内示をIRecommendOrderArrivalから生成する
const getFourMonthSupplierAnnouncesFromArrivals = computed(() => {
  const announces: IAnnounce[] = [];
  // arrival.crossDateがundefinedだった時用
  const zeroYearMonthInfo: IYearMonth = {
    year: 0,
    month: 0,
  };

  // 作成年月選択が行われていなかったら、処理を飛ばす
  if (!state.selectedYearMonth) return;

  const selectedDate = DateService.convertIYearMonthToDate(state.selectedYearMonth);
  ForecastService.setAnnounceYearMonth(announces, selectedDate, false, FORECAST_LENGTH_IN_MODAL);

  if (state.useCustomerAnnounce) {
    // 客先内示をそのまま仕入先内示にする
    props.customerAnnounce?.forEach((customerAnnounce) => {
      const announce = announces.find((announce) => DateService.equalYearMonthInfo(announce, customerAnnounce));
      if (announce) {
        announce.amount = customerAnnounce.amount;
      }
    });
  } else {
    // シミュレーションの結果で得られた納期を元に仕入先内示を埋める
    props.recommend?.forEach((arrival) => {
      const announce = announces.find((announce) => DateService.equalYearMonthInfo(announce, arrival.crossDate ?? zeroYearMonthInfo));
      if (announce) {
        announce.amount += arrival.amount;
      }
    });
  }
  // 仕入先内示を更新する
  store.updateSupplierAnnounce(announces);
  return announces;
});

/**
 * 選択された年月情報が作成年月である仕入先内示情報で埋める
 */
const filterAndFillSupplierAnnouncements = computed(() => {
  const announces: IAnnounce[] = [];

  // 年月が選択されていなかったら、飛ばす
  if (!state.selectedYearMonth) return;

  ForecastService.setAnnounceYearMonth(
    announces,
    DateService.convertIYearMonthToDate(state.selectedYearMonth),
    false,
    FORECAST_LENGTH_IN_MODAL
  );

  // YearMonthPicker選択値-2が作成年月の年月情報を作成する
  const updatedYearMonth: IYearMonth = DateService.getShiftedIYearMonthByMonths(
    simpleCopy(state.selectedYearMonth.year),
    simpleCopy(state.selectedYearMonth.month),
    -2
  );

  // createdYearMonthと一致する作成年月の仕入先内示情報を絞り込む
  const filteredSupplierAnnounce = props.supplierAnnounce?.filter(
    (supplierAnnounce) =>
      supplierAnnounce.manageInitiatedYearMonth != null &&
      DateService.equalYearMonthInfo(supplierAnnounce.manageInitiatedYearMonth, updatedYearMonth)
  );

  // filter後の仕入先内示情報で表示用のオブジェクトを埋める
  filteredSupplierAnnounce?.forEach((it) => {
    const announce = announces.find((announce) => DateService.equalYearMonthInfo(announce, it));
    if (announce) {
      announce.amount = it.amount;
    }
  });
  return announces;
});

/**
 * 客先内示確定ボタンを押した時の処理
 */
const onConfirmReceiveAnnounce = () => {
  store.confirmReceiveAnnounce();
};

/**
 * アクティブな進捗状況かどうか
 * @param progress - 進捗状況
 */
const isActive = (progress: RecommendEditProgress) => {
  return !store.state.errorType && props.progress === progress;
};

/**
 * 表示情報を更新する
 */
const updateDisplayBusiness = async () => {
  // 確認モーダルを表示
  const modalResult = await openConfirmModal(
    'question',
    t('表示情報更新(詳細ページ)'),
    t('変更する(詳細ページ)'),
    t('キャンセル'),
    t('表示情報を変更しますか(詳細ページ)')
  );
  if (!modalResult.isConfirmed) return;
  emits('updateBusiness');
};

/**
 * 推奨発注のチェックボックスの状態を変更する
 * @param checkedIndex - チェックボックスのインデックス
 */
const onCheckedOrder = (checkedIndex: number[]) => {
  state.checkedIndex = checkedIndex;
  store.state.checkedIndex = checkedIndex;
};

/**
 * 登録後の発注情報をストアから同期する
 */
const syncRegisteredRecommendOrder = () => {
  state.recommendOrderForms = simpleCopy(store.state.recommend);
};

/**
 * 最終チェック情報のユーザー名が省略されているかをチェックする関数
 * @param e - MouseEvent
 */
const mouseoverLastCheckedUserName = (e: MouseEvent) => {
  const { target } = e;
  if (!(target instanceof HTMLElement)) return;
  state.isLastCheckedUserNameEllipsis = target.offsetWidth < target.scrollWidth;
};
</script>

<template>
  <div class="recommend-edit">
    <div class="top-area">
      <div class="top-area__left">
        <TextButton
          v-if="isActive('EditCompleted')"
          class="top-area__all-initialize-button"
          :button-style="'btn-secondary'"
          :size="'btn-sm'"
          @click="updateDisplayBusiness"
        >
          {{ t('更新') }}
        </TextButton>
        <button
          v-else
          class="top-area__all-initialize-button btn-text"
          :class="!store?.state.errorType ? '' : 'top-area__all-initialize-button--inactive'"
          @click="store?.initializeAll"
        >
          {{ t('すべての値を初期化(詳細ページ)') }}
        </button>
      </div>

      <div class="top-area__right">
        <div>{{ t('詳細ページ：最終チェック日時') }}</div>
        <div>
          {{ lastCheckedAt }}
        </div>
        <div
          v-tooltip.bottom="state.isLastCheckedUserNameEllipsis ? store.state.formSet.lastCheckedUser?.name : undefined"
          class="top-area__checked-info-user-name"
          @mouseover="mouseoverLastCheckedUserName"
        >
          {{ store.state.formSet.lastCheckedUser?.name ?? '----' }}
        </div>
        <TextIconButton
          :text="t('詳細ページ：確認済み')"
          icon="checkWhite"
          is-icon-left-side
          class="top-area__checked-button"
          @click="store.updateLastChecked()"
        />
      </div>
    </div>

    <RecommendEditWrapper
      :is-active="isActive('EditReceiveAnnounce')"
      :title="t('客先内示')"
      :expanded-less-desc="t('客先内示パネルを折りたたむ(詳細ページ)')"
      :expanded-more-desc="t('客先内示パネルを展開する(詳細ページ)')"
    >
      <RecommendEditReceiveAnnounce
        :prop-calculated-announce-values="getFourMonthCustomerAnnouncesFromArrivals"
        :prop-lead-time="propExtraSimulate?.extraLeadTime ?? 0"
        :prop-lead-time-display-value="store?.state.selectedLeadTimeDisplayValue"
        :prop-lead-time-unit="store?.state.selectedLeadTimeUnit"
        :prop-std-index-infos="propExtraSimulate?.extraStdInventory ?? emptyInventory"
        @confirm-receive-announce="onConfirmReceiveAnnounce"
      />
    </RecommendEditWrapper>

    <RecommendEditWrapper
      :is-active="isActive('EditRecommend')"
      :title="t('仕入先への発注計画(詳細ページ)')"
      :expanded-less-desc="t('発注計画パネルを折りたたむ(詳細ページ)')"
      :expanded-more-desc="t('発注計画パネルを展開する(詳細ページ)')"
      is-growing
      resizable
      :resize-state="store.state.recommendEditDefinedOrderResizeState"
      @update:resize-state="store.state.recommendEditDefinedOrderResizeState = $event"
    >
      <RecommendEditDefinedOrder
        ref="definedOrder"
        :recommend-order-forms="state.recommendOrderForms"
        :partial-delivery-forms="state.partialDeliveryForms"
        :checked-index="state.checkedIndex"
        :scroll-position="definedOrderScrollPosition"
        @changed-check-box-state="onCheckedOrder($event)"
        @registered-recommend-order="syncRegisteredRecommendOrder"
      />
    </RecommendEditWrapper>

    <RecommendEditWrapper
      :is-active="isActive('EditSupplierAnnounce')"
      :title="t('仕入先内示')"
      :expanded-less-desc="t('仕入先内示パネルを折りたたむ(詳細ページ)')"
      :expanded-more-desc="t('仕入先内示パネルを展開する(詳細ページ)')"
    >
      <RecommendEditSupplierAnnounce
        :calculated-announce-values="getFourMonthSupplierAnnouncesFromArrivals ?? []"
        :announce-histories="filterAndFillSupplierAnnouncements ?? []"
        :receive-year-month="state.selectedYearMonth"
        :months-register-supplier-forecast="state.monthsRegisterSupplierForecast"
        @change-status="state.useCustomerAnnounce = $event"
        @change-year-month="state.selectedYearMonth = $event"
        @change-months-register-supplier-forecast="state.monthsRegisterSupplierForecast = $event"
      />
    </RecommendEditWrapper>
  </div>
</template>

<style lang="scss" scoped>
.recommend-edit {
  display: flex;
  flex-direction: column;
  height: 100%;
  row-gap: 8px;
  padding: 16px 8px 8px 8px;
  overflow-y: scroll;
}

.top-area {
  display: grid;
  grid-template-columns: max-content 1fr;
  align-content: end;
  max-height: 24px;
  padding-left: 16px;
  gap: 8px;
  &__left {
    display: flex;
    gap: 8px;
    max-height: 24px;
    align-items: center;
  }

  &__all-initialize-button {
    color: $semanticPrimary;
    font-size: $fontSizeM;
    font-weight: bold;

    &--inactive {
      opacity: $transparency50;
      pointer-events: none;
    }
  }

  &__right {
    display: grid;
    grid-template-columns: repeat(4, auto);
    gap: 8px;
    justify-self: end;
    align-items: center;
    font-size: 14px;
    font-family: 'Noto Sans JP';
  }

  &__checked-info-user-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__checked-button {
    height: 20px;
    min-width: 72px;
    padding: 0 4px;
    font-size: 12px;
    line-height: 1;
    font-weight: 400;
    gap: 0;
  }
}
</style>
